'use client';

/**
 * Interactive editor for BlockSlide.
 *
 * Read-only-ish canvas: selection + inline text editing only. There is no
 * drag-and-drop and no resize handle — every move is performed via the
 * BlockToolbar buttons (anchor 3×3 picker, ↑/↓ stack reorder, W±, etc.).
 * Result: the canvas stays uncluttered and the slide looks the same as the
 * exported PDF.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import type { Anchor, Block } from '@/lib/deck/blocks';
import { ANCHOR_POSITIONS, anchorParts } from '@/lib/deck/blocks';
import {
  duplicateSlideBlock,
  flipSlideHorizontally,
  patchSlideBlock,
  removeSlideBlock,
  reorderBlockWithinAnchor,
} from '@/lib/deck/block-helpers';
import { createId } from '@/lib/deck/storage';
import type { BlockSlide } from '@/lib/deck/types';
import { getBgFocal, patchBgFocal } from '@/lib/deck/bg-helpers';
import {
  BlockContent,
  DeckSlideChrome,
  SLIDE_PX_PER_PCT,
  anchorCellStyle,
  blockLayoutStyle,
  groupBlocksByAnchor,
  toneFromSlide,
} from './BlockSlide';

function clamp(v: number, lo: number, hi: number) {
  return Math.max(lo, Math.min(hi, v));
}

function isTextBlock(b: Block): b is Extract<Block, { text: string }> {
  return (
    b.kind === 'eyebrow' ||
    b.kind === 'headline' ||
    b.kind === 'subheadline' ||
    b.kind === 'lede' ||
    b.kind === 'body' ||
    b.kind === 'caption' ||
    b.kind === 'accent'
  );
}

function defaultFontSize(kind: Extract<Block, { text: string }>['kind']): number {
  switch (kind) {
    case 'headline':
      return 48;
    case 'subheadline':
      return 28;
    case 'lede':
      return 22;
    case 'body':
      return 18;
    case 'caption':
      return 14;
    case 'accent':
      return 16;
    default:
      return 14;
  }
}

interface BlockCanvasProps {
  slide: BlockSlide;
  onChange: (slide: BlockSlide) => void;
  selectedId: string | null;
  onSelectId: (id: string | null) => void;
  /** Open the BackgroundPicker modal — owned by the parent editor. */
  onChangeBackground?: () => void;
}

export function BlockCanvas({
  slide,
  onChange,
  selectedId,
  onSelectId,
  onChangeBackground,
}: BlockCanvasProps) {
  const tone = toneFromSlide(slide.textColor);
  const selected = slide.blocks.find((b) => b.id === selectedId) ?? null;
  const groups = groupBlocksByAnchor(slide.blocks);

  // Background context menu (right-click on the slide background — not on a
  // block). Anchored to the cursor position so it appears exactly where the
  // user clicked.
  const [bgMenu, setBgMenu] = useState<{ x: number; y: number } | null>(null);
  const bgPanRef = useRef<{
    startX: number;
    startY: number;
    focalX: number;
    focalY: number;
    moved: boolean;
  } | null>(null);

  useEffect(() => {
    if (!bgMenu) return;
    const close = () => setBgMenu(null);
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    window.addEventListener('pointerdown', close);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('pointerdown', close);
      window.removeEventListener('keydown', onKey);
    };
  }, [bgMenu]);

  const patchBlock = useCallback(
    (id: string, patch: Partial<Block>) => {
      onChange(patchSlideBlock(slide, id, patch));
    },
    [onChange, slide],
  );

  const onBulletsItemChange = useCallback(
    (blockId: string, itemIndex: number, text: string) => {
      const block = slide.blocks.find((b) => b.id === blockId);
      if (!block || block.kind !== 'bullets') return;
      const items = [...block.items];
      if (itemIndex < 0 || itemIndex >= items.length) return;
      items[itemIndex] = text;
      patchBlock(blockId, { items } as Partial<Block>);
    },
    [patchBlock, slide],
  );

  const onTextChange = useCallback(
    (blockId: string, text: string) => {
      patchBlock(blockId, { text } as Partial<Block>);
    },
    [patchBlock],
  );

  const onQrChange = useCallback(
    (blockId: string, patch: { value?: string; caption?: string }) => {
      patchBlock(blockId, patch as Partial<Block>);
    },
    [patchBlock],
  );

  // ── Background actions (used by the right-click menu) ─────────────────
  const startEdgeResize = useCallback(
    (block: Block, side: 'left' | 'right', e: React.PointerEvent) => {
      e.preventDefault();
      e.stopPropagation();
      onSelectId(block.id);

      const handleEl = e.currentTarget as HTMLElement;
      const blockEl = handleEl.closest('[data-block-shell]') as HTMLElement | null;
      const slideEl = blockEl?.closest('.deck-fixed-frame') as HTMLElement | null;
      if (!blockEl || !slideEl) return;

      const slideRect = slideEl.getBoundingClientRect();
      if (slideRect.width === 0) return;
      const blockRect = blockEl.getBoundingClientRect();
      const startW = (blockRect.width / slideRect.width) * 100;
      const startMx = e.clientX;

      const onMove = (ev: PointerEvent) => {
        const dxScreen = ev.clientX - startMx;
        const dxPct = (dxScreen / slideRect.width) * 100;
        // Right handle widens on rightward drag; left handle widens on leftward drag.
        const nw = clamp(startW + (side === 'right' ? dxPct : -dxPct), 8, 100);
        patchBlock(block.id, { w: nw } as Partial<Block>);
      };
      const onUp = () => {
        window.removeEventListener('pointermove', onMove);
        window.removeEventListener('pointerup', onUp);
        window.removeEventListener('pointercancel', onUp);
      };
      window.addEventListener('pointermove', onMove);
      window.addEventListener('pointerup', onUp);
      window.addEventListener('pointercancel', onUp);
    },
    [onSelectId, patchBlock],
  );

  // ── Background actions (used by the right-click menu) ─────────────────
  const removeBgImage = () => {
    if (!slide.bg?.src) return;
    const next = { ...slide, bg: { ...slide.bg } };
    delete next.bg.src;
    onChange(next);
  };

  const setBgColor = (color: string) => {
    onChange({ ...slide, bg: { ...(slide.bg ?? {}), color } });
  };

  const clearBg = () => {
    onChange({ ...slide, bg: undefined });
  };

  const flipSlide = () => {
    onChange(flipSlideHorizontally(slide));
  };

  const startBgPan = useCallback(
    (e: React.PointerEvent) => {
      if (!slide.bg?.src) return;
      e.preventDefault();
      e.stopPropagation();
      onSelectId(null);
      const slideEl = (e.currentTarget as HTMLElement).closest('.deck-fixed-frame') as HTMLElement | null;
      if (!slideEl) return;
      const rect = slideEl.getBoundingClientRect();
      if (rect.width === 0) return;
      const focal = getBgFocal(slide.bg);
      bgPanRef.current = {
        startX: e.clientX,
        startY: e.clientY,
        focalX: focal.x,
        focalY: focal.y,
        moved: false,
      };

      const onMove = (ev: PointerEvent) => {
        const session = bgPanRef.current;
        if (!session) return;
        const dx = ((ev.clientX - session.startX) / rect.width) * 100;
        const dy = ((ev.clientY - session.startY) / rect.height) * 100;
        if (Math.abs(dx) > 0.5 || Math.abs(dy) > 0.5) session.moved = true;
        onChange({
          ...slide,
          bg: patchBgFocal(slide.bg!, session.focalX - dx, session.focalY - dy),
        });
      };

      const onUp = () => {
        const session = bgPanRef.current;
        bgPanRef.current = null;
        window.removeEventListener('pointermove', onMove);
        window.removeEventListener('pointerup', onUp);
        window.removeEventListener('pointercancel', onUp);
        if (session && !session.moved) onChangeBackground?.();
      };

      window.addEventListener('pointermove', onMove);
      window.addEventListener('pointerup', onUp);
      window.addEventListener('pointercancel', onUp);
    },
    [slide, onChange, onSelectId, onChangeBackground],
  );

  const handleBgContextMenu = (e: React.MouseEvent) => {
    // Ignore right-clicks landing on a block — those should fall back to the
    // block toolbar / native menu (or do nothing). Only the slide background
    // opens this menu.
    const t = e.target as HTMLElement;
    if (t.closest('[data-block-shell]')) return;
    e.preventDefault();
    setBgMenu({ x: e.clientX, y: e.clientY });
  };

  return (
    <div
      className="relative h-full w-full"
      onPointerDown={(e) => {
        // Click on empty canvas → deselect.
        if (e.target === e.currentTarget) onSelectId(null);
      }}
      onContextMenu={handleBgContextMenu}
    >
      <DeckSlideChrome slide={slide}>
        {slide.bg?.src && (
          <div
            data-deck-bg-pan
            className="absolute inset-0 z-[5] cursor-grab active:cursor-grabbing"
            aria-hidden
            onPointerDown={startBgPan}
          />
        )}
        {(Object.keys(groups) as (keyof typeof groups)[]).map((anchor) => {
          const blocks = groups[anchor];
          if (!blocks?.length) return null;
          return (
            <div key={anchor} data-anchor-cell={anchor} style={anchorCellStyle(anchor)}>
              {blocks.map((block, i) => {
                const isSelected = block.id === selectedId;
                const handles = handleSidesForAnchor(anchor);
                return (
                  <div
                    key={block.id}
                    data-block-shell
                    style={blockLayoutStyle(block, i === blocks.length - 1)}
                    onPointerDown={(e) => {
                      const t = e.target as HTMLElement;
                      if (t.closest('[data-resize-handle]')) return;
                      e.stopPropagation();
                      onSelectId(block.id);
                    }}
                    className={`pointer-events-auto relative cursor-pointer transition ${
                      isSelected
                        ? 'outline outline-2 outline-offset-4 outline-marine-green'
                        : 'hover:outline hover:outline-1 hover:outline-offset-4 hover:outline-marine-green/40'
                    }`}
                  >
      <BlockContent
                      block={block}
                      tone={tone}
                      onTextChange={onTextChange}
                      onQrChange={onQrChange}
                      onBulletsItemChange={onBulletsItemChange}
                    />
                    {isSelected && 'w' in block && (
                      <>
                        {handles.includes('right') && (
                          <ResizeHandle
                            side="right"
                            onPointerDown={(e) => startEdgeResize(block, 'right', e)}
                          />
                        )}
                        {handles.includes('left') && (
                          <ResizeHandle
                            side="left"
                            onPointerDown={(e) => startEdgeResize(block, 'left', e)}
                          />
                        )}
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          );
        })}
      </DeckSlideChrome>

      {bgMenu && (
        <BgContextMenu
          x={bgMenu.x}
          y={bgMenu.y}
          hasImage={Boolean(slide.bg?.src)}
          hasBg={Boolean(slide.bg)}
          color={slide.bg?.color}
          onChangeImage={
            onChangeBackground
              ? () => {
                  setBgMenu(null);
                  onChangeBackground();
                }
              : undefined
          }
          onRemoveImage={() => {
            removeBgImage();
            setBgMenu(null);
          }}
          onSetColor={(c) => {
            setBgColor(c);
            setBgMenu(null);
          }}
          onClearBg={() => {
            clearBg();
            setBgMenu(null);
          }}
          onFlipSlide={() => {
            flipSlide();
            setBgMenu(null);
          }}
          onClose={() => setBgMenu(null)}
        />
      )}
    </div>
  );
}

export { SLIDE_PX_PER_PCT };

/**
 * Returns which edges should expose a resize handle given the cell's anchor.
 *   left-anchored cells  → handle on the right edge
 *   right-anchored cells → handle on the left edge
 *   centered cells       → handles on both edges (the block grows symmetrically)
 */
function handleSidesForAnchor(anchor: Anchor): Array<'left' | 'right'> {
  const { h } = anchorParts(anchor);
  if (h === 'left') return ['right'];
  if (h === 'right') return ['left'];
  return ['left', 'right'];
}

// ── Background context menu ─────────────────────────────────────────────
const BG_PRESET_COLORS: { name: string; value: string }[] = [
  { name: 'Ink', value: '#0D0D14' },
  { name: 'Navy', value: '#0c1a3a' },
  { name: 'Marine green', value: '#1AC799' },
  { name: 'Cream', value: '#FCF7F1' },
  { name: 'Coral', value: '#F16764' },
  { name: 'White', value: '#FFFFFF' },
];

function BgContextMenu({
  x,
  y,
  hasImage,
  hasBg,
  color,
  onChangeImage,
  onRemoveImage,
  onSetColor,
  onClearBg,
  onFlipSlide,
  onClose,
}: {
  x: number;
  y: number;
  hasImage: boolean;
  hasBg: boolean;
  color?: string;
  onChangeImage?: () => void;
  onRemoveImage: () => void;
  onSetColor: (c: string) => void;
  onClearBg: () => void;
  onFlipSlide: () => void;
  onClose: () => void;
}) {
  // Clamp the menu inside the viewport so it doesn't render off-screen when
  // the user right-clicks near the bottom-right edge.
  const W = 240;
  const H = 360;
  const left = Math.min(x, window.innerWidth - W - 8);
  const top = Math.min(y, window.innerHeight - H - 8);

  return (
    <div
      role="menu"
      aria-label="Background"
      className="fixed z-50 flex w-60 flex-col overflow-hidden rounded-xl border border-beige bg-white text-[12px] shadow-2xl"
      style={{ left, top }}
      // Stop the global pointerdown handler from closing before clicks register.
      onPointerDown={(e) => e.stopPropagation()}
      onContextMenu={(e) => e.preventDefault()}
    >
      <div className="border-b border-beige bg-cream-alt px-3 py-2 font-dank-mono text-[10px] uppercase tracking-widest text-slate-500">
        Background
      </div>

      <MenuItem
        primary
        onClick={onChangeImage}
        disabled={!onChangeImage}
        label="Change background image…"
        kbd="↩"
      />
      <MenuItem onClick={onRemoveImage} disabled={!hasImage} label="Remove background image" />

      <div className="border-t border-beige">
        <MenuItem onClick={onFlipSlide} label="Flip slide horizontally ⇄" />
      </div>

      <div className="border-t border-beige px-3 py-2">
        <p className="mb-2 font-dank-mono text-[10px] uppercase tracking-widest text-slate-400">
          Fill colour
        </p>
        <div className="flex flex-wrap items-center gap-1.5">
          {BG_PRESET_COLORS.map((c) => {
            const active = color?.toLowerCase() === c.value.toLowerCase();
            return (
              <button
                key={c.value}
                type="button"
                onClick={() => onSetColor(c.value)}
                title={c.name}
                aria-label={c.name}
                className={`h-6 w-6 rounded-full border transition-transform hover:scale-110 ${
                  active ? 'border-marine-green ring-2 ring-marine-green/40' : 'border-[#2E3245]/20'
                }`}
                style={{ backgroundColor: c.value }}
              />
            );
          })}
          <label className="ml-1 inline-flex h-6 cursor-pointer items-center gap-1 rounded-full border border-beige px-2 text-[10px] text-slate-500 hover:border-marine-green">
            <span>Custom</span>
            <input
              type="color"
              value={color ?? '#000000'}
              onChange={(e) => onSetColor(e.target.value)}
              className="h-4 w-4 cursor-pointer rounded border-none bg-transparent p-0"
            />
          </label>
        </div>
      </div>

      <MenuItem onClick={onClearBg} disabled={!hasBg} label="Reset background" />

      <button
        type="button"
        onClick={onClose}
        className="border-t border-beige bg-cream-alt px-3 py-1.5 font-dank-mono text-[10px] uppercase tracking-widest text-slate-400 hover:text-slate-700"
      >
        Close
      </button>
    </div>
  );
}

function MenuItem({
  primary,
  label,
  kbd,
  onClick,
  disabled,
}: {
  primary?: boolean;
  label: string;
  kbd?: string;
  onClick?: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      role="menuitem"
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      className={`flex items-center justify-between px-3 py-2 text-left transition ${
        disabled
          ? 'cursor-not-allowed text-slate-300'
          : primary
            ? 'font-semibold text-marine-green hover:bg-marine-green/10'
            : 'text-slate-700 hover:bg-cream-alt'
      }`}
    >
      <span>{label}</span>
      {kbd && <span className="font-dank-mono text-[10px] text-slate-300">{kbd}</span>}
    </button>
  );
}

function ResizeHandle({
  side,
  onPointerDown,
}: {
  side: 'left' | 'right';
  onPointerDown: (e: React.PointerEvent) => void;
}) {
  const sideClass = side === 'right' ? 'right-[-10px]' : 'left-[-10px]';
  return (
    <div
      data-resize-handle
      role="separator"
      aria-orientation="vertical"
      aria-label={`Resize from ${side} edge`}
      title={`Drag to resize width (${side} edge)`}
      onPointerDown={onPointerDown}
      onClick={(e) => e.stopPropagation()}
      className={`absolute ${sideClass} top-1/2 z-30 flex h-16 w-5 -translate-y-1/2 cursor-ew-resize items-center justify-center rounded-full`}
    >
      <span className="block h-12 w-1.5 rounded-full bg-marine-green shadow-md ring-2 ring-white/80" />
    </div>
  );
}
